/*----------------------------------
- APPRAISE FEATURE PAGE
----------------------------------*/

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import React from 'react';

import { APPRAISAL_PROVIDERS } from '@uniquedomains/core/domains/appraisal';
import CtaPill from '@uniquedomains/components/website/WebsiteCta';
import type { AskYourAIProps } from '@uniquedomains/components/ask-ai';

import type { LandingHeaderDomainsNavigation } from '@/client/catalogs/landing/pageContent';
import { PRODUCT_APPRAISE_PAGE_CONTENT } from '@/client/catalogs/landing/product/appraise/pageContent';
import { EMPTY_LANDING_NAVIGATION } from '@/client/pages/landing/types';
import { Footer } from '@/client/pages/landing/components/layout/Footer';
import { Header, type HeaderMobileActions } from '@/client/pages/landing/components/layout/Header';
import {
    EYEBROW_CLASS,
    HERO_SECTION_CLASS,
    PAGE_CONTAINER_CLASS,
    PAGE_LEAD_CLASS,
    PAGE_TITLE_CLASS,
    SECTION_LEAD_CLASS,
    SECTION_SPACING_CLASS,
    SECTION_TITLE_CLASS,
} from '@/client/pages/landing/components/shared/pageStyles';
import RelatedLinks from '@/client/pages/landing/components/navigation/RelatedLinks';
import { FAQ, FAQ_SECTION_CLASS } from '@/client/pages/landing/components/sections/faq/FAQ';
import HomeAskAiSection from '@/client/pages/landing/components/ask-ai/HomeAskAiSection';
import PageGeoBlock from '@/client/pages/landing/components/geo/PageGeoBlock';
import { buildProductAppraiseGeoContext } from '@/client/pages/landing/components/geo/pageGeoContexts';
import { LANDING_WEBSITE_LINKS } from '@/client/catalogs/landing/publicLinks';
import { LANDING_WEBSITE_PAGE_ROUTES } from '@/common/catalogs/landing/publicPages';
import { getHumanPageRelationships } from '@/common/catalogs/landing/internalLinks';
import { track } from '@/client/pages/landing/lib/analytics';
import { trackConversionTrigger } from '@/client/pages/landing/lib/trackConversionTrigger';
import { trackLandingCta } from '@/client/pages/landing/lib/trackLandingCta';
import type { ConversionTriggerId } from '@uniquedomains/components/contracts/analytics/conversionTriggers';
import AppraiseComposerSection, { buildAppraiseGateHref, trackAppraiseGate } from './sections/AppraiseComposerSection';
import ConsensusChart from './sections/ConsensusChart';
import SourceAngleGrid from './sections/SourceAngleGrid';
import SourceComparisonTable from './sections/SourceComparisonTable';

import '../../../shared.css';

/*----------------------------------
- TYPES
----------------------------------*/

type ProductAppraisePageProps = {
    navigation?: LandingHeaderDomainsNavigation;
};

/*----------------------------------
- CONSTANTS
----------------------------------*/

const TRACKING_PAGE = 'product_appraise';

/** Two columns from `lg` up: the argument on the left, the figure on the right. */
const SPLIT_SECTION_CLASS = 'lg:grid lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] lg:gap-16';
const FACT_CARD_CLASS = 'rounded-[18px] bg-white/70 px-3.5 py-4 lg:p-4';

/**
 * The four figures under the hero, read from the provider catalog rather than typed:
 * how many sources exist, the one axis they are plotted on, and the channel count and
 * batch size of the range model, which is the catalog's maximum on both counts.
 */
const buildHeroFacts = (labels: (typeof PRODUCT_APPRAISE_PAGE_CONTENT)['inShort']['factLabels']) => [
    { value: APPRAISAL_PROVIDERS.length, label: labels.sources },
    { value: 1, label: labels.axis },
    { value: Math.max(...APPRAISAL_PROVIDERS.map((provider) => provider.channels.length)), label: labels.channels },
    { value: Math.max(...APPRAISAL_PROVIDERS.map((provider) => provider.batchSize)), label: labels.batch },
];

/*----------------------------------
- COMPONENT
----------------------------------*/

const ProductAppraisePage = ({ navigation = EMPTY_LANDING_NAVIGATION }: ProductAppraisePageProps) => {
    /*----------------------------------
    - INIT
    ----------------------------------*/

    const content = PRODUCT_APPRAISE_PAGE_CONTENT;
    const links = LANDING_WEBSITE_LINKS;
    const facts = buildHeroFacts(content.inShort.factLabels);
    const relatedLinks = getHumanPageRelationships(LANDING_WEBSITE_PAGE_ROUTES.productAppraise).map(
        ({ label, targetPath }) => ({ label, href: targetPath }),
    );
    const geoContext = React.useMemo(() => buildProductAppraiseGeoContext(), []);
    const askAiContext = React.useMemo<AskYourAIProps>(
        () => ({
            pageType: 'guide',
            intent: 'evaluate_product',
            pageUrl: LANDING_WEBSITE_PAGE_ROUTES.productAppraise,
            pageTitle: 'Unique Domains Appraise feature',
            geoContext,
        }),
        [geoContext],
    );

    // The phone's primary action is the composer's run button: same signup gate, same
    // tracking, so the bar cannot become a second, differently measured path.
    const gateHref = buildAppraiseGateHref();
    const mobileActions: HeaderMobileActions = {
        primary: {
            label: content.mobileActionLabel,
            href: gateHref,
            onClick: () => trackAppraiseGate({ page: TRACKING_PAGE, action: 'action_bar_run', href: gateHref }),
            testId: 'mobile-action-bar-primary',
        },
    };

    /*----------------------------------
    - EFFECTS
    ----------------------------------*/

    React.useEffect(() => {
        if (typeof window === 'undefined') return;

        track('page_view', {
            page: TRACKING_PAGE,
            path: window.location.pathname + window.location.search,
        });

        void import('@/client/pages/landing/lib/reveal').then(({ initScrollReveal }) => {
            initScrollReveal();
        });
    }, []);

    /*----------------------------------
    - ACTIONS
    ----------------------------------*/

    const trackCta = (
        { section, label, kind, href, triggerId }: {
            section: string;
            label: string;
            kind: string;
            href: string;
            triggerId: ConversionTriggerId;
        },
    ) => {
        trackLandingCta({
            page: TRACKING_PAGE,
            section,
            label,
            kind,
            href,
            surface: 'product_appraise_page',
        });
        trackConversionTrigger(triggerId, { href, section });
    };

    /*----------------------------------
    - RENDER
    ----------------------------------*/

    return (
        <div class="ud-landing-shell min-h-screen text-slate-950">
            <div class="relative z-10">
                <Header
                    links={links}
                    navigation={navigation}
                    currentPath={LANDING_WEBSITE_PAGE_ROUTES.productAppraise}
                    trackingPage={TRACKING_PAGE}
                    mobileActions={mobileActions}
                />

                <main>
                    {/* HERO */}
                    {/* The composer is the conversion entry, so it sits in the hero itself
                        rather than in a section under it. A column on a phone rather than a
                        one-column grid: a grid track sizes to the composer's intrinsic width
                        and pushes the hero past the screen edge, while a stretched flex item
                        stays at the container width and lets its run card wrap. */}
                    <section
                        class={`${PAGE_CONTAINER_CLASS} ${HERO_SECTION_CLASS} flex flex-col gap-y-[22px] lg:grid lg:grid-cols-[minmax(0,1fr)_520px] lg:gap-x-16 lg:gap-y-0`}
                    >
                        <div data-reveal>
                            <h1 class={PAGE_TITLE_CLASS}>
                                {content.hero.titleLead} <span class="text-[#66696b]">{content.hero.titleTrail}</span>
                            </h1>
                            <p class={`${PAGE_LEAD_CLASS} mt-4 max-w-[560px] lg:mt-[22px]`}>{content.hero.body}</p>
                        </div>

                        <AppraiseComposerSection page={TRACKING_PAGE} class="lg:row-span-2 lg:self-center" />

                        <div data-reveal class="mt-[22px] lg:mt-7">
                            {/* The band is labelled only on a phone, where it reads as its own
                                section under the composer. On desktop it closes the hero. */}
                            <p class={`${EYEBROW_CLASS} lg:hidden`}>{content.inShort.eyebrow}</p>
                            <dl class="mt-2.5 grid grid-cols-2 gap-2 lg:mt-0 lg:grid-cols-4 lg:gap-2.5">
                                {facts.map((fact) => (
                                    <div key={fact.label} class={FACT_CARD_CLASS}>
                                        <dt class="text-[34px] font-light leading-none tracking-[-0.045em] tabular-nums">
                                            {fact.value}
                                        </dt>
                                        <dd class="mt-2 text-xs font-semibold leading-[1.4] text-[#66696b]">
                                            {fact.label}
                                        </dd>
                                    </div>
                                ))}
                            </dl>
                        </div>
                    </section>

                    {/* CONSENSUS */}
                    <section
                        class={`${PAGE_CONTAINER_CLASS} ${SECTION_SPACING_CLASS} ${SPLIT_SECTION_CLASS} flex flex-col gap-[18px] lg:items-center`}
                    >
                        <div data-reveal>
                            <h2 class={SECTION_TITLE_CLASS}>{content.consensus.title}</h2>
                            <p class={SECTION_LEAD_CLASS}>{content.consensus.lead}</p>
                        </div>
                        <ConsensusChart content={content.consensus} />
                    </section>

                    {/* ANGLES */}
                    <section class={`${PAGE_CONTAINER_CLASS} ${SECTION_SPACING_CLASS}`}>
                        <div data-reveal class="lg:max-w-[760px]">
                            <h2 class={SECTION_TITLE_CLASS}>{content.angles.title}</h2>
                            <p class={SECTION_LEAD_CLASS}>{content.angles.body}</p>
                        </div>
                        <SourceAngleGrid items={content.sources} labels={content.angles} />
                    </section>

                    {/* STEPS */}
                    <section class={`${PAGE_CONTAINER_CLASS} ${SECTION_SPACING_CLASS} ${SPLIT_SECTION_CLASS} lg:items-start`}>
                        <h2 data-reveal class={`${SECTION_TITLE_CLASS} lg:sticky lg:top-[120px] lg:self-start`}>
                            {content.steps.title}
                        </h2>
                        <div class="mt-2 lg:mt-0">
                            {content.steps.items.map((step) => (
                                <div
                                    key={step.step}
                                    data-reveal
                                    class="grid grid-cols-[auto_1fr] gap-3.5 border-b border-[#e6e3db] py-[18px] lg:gap-5 lg:py-[26px]"
                                >
                                    <span class="inline-flex h-[34px] w-[34px] items-center justify-center rounded-full bg-[#1b1d1e] text-xs font-extrabold text-white lg:h-10 lg:w-10 lg:text-[13px]">
                                        {step.step}
                                    </span>
                                    <div class="min-w-0">
                                        <h3 class="mt-[5px] text-[17px] font-bold tracking-[-0.015em] lg:mt-[7px] lg:text-[21px] lg:tracking-[-0.02em]">
                                            {step.title}
                                        </h3>
                                        <p class="mt-1.5 text-sm leading-[1.6] text-[#66696b] lg:mt-2 lg:text-[15.5px] lg:leading-[1.65]">
                                            {step.body}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* SOURCES COMPARED */}
                    <section class={`${PAGE_CONTAINER_CLASS} ${SECTION_SPACING_CLASS}`}>
                        <div data-reveal class="lg:max-w-[760px]">
                            <h2 class={SECTION_TITLE_CLASS}>{content.comparison.title}</h2>
                            <p class={SECTION_LEAD_CLASS}>{content.comparison.body}</p>
                        </div>
                        <SourceComparisonTable headers={content.comparison.headers} rows={content.sources} />
                        <div data-reveal class="mt-6 lg:mt-8">
                            <CtaPill
                                href={`${links.pricing}#plans`}
                                variant="light"
                                onClick={() =>
                                    trackCta({
                                        section: 'sources',
                                        label: content.comparison.ctaLabel,
                                        kind: 'pricing',
                                        href: `${links.pricing}#plans`,
                                        triggerId: 'website_product_appraise_sources_cta',
                                    })
                                }
                            >
                                {content.comparison.ctaLabel}
                            </CtaPill>
                        </div>
                    </section>

                    {/* FAQ */}
                    <FAQ
                        title={content.faq.title}
                        description={content.faq.body}
                        items={content.faq.items}
                        surface="panel"
                        sectionClassName={FAQ_SECTION_CLASS}
                        contactHref={LANDING_WEBSITE_PAGE_ROUTES.team}
                        contactLabel={content.faq.ctaLabel}
                        onContactClick={() =>
                            trackCta({
                                section: 'faq',
                                label: content.faq.ctaLabel,
                                kind: 'support',
                                href: LANDING_WEBSITE_PAGE_ROUTES.team,
                                triggerId: 'website_product_appraise_faq_contact',
                            })
                        }
                        showItemLinks
                        aside={
                            <>
                                {/* GLOSSARY */}
                                {/* Under the FAQ heading on desktop and after the answers on a phone:
                                    the shared FAQ places its aside in both spots from one node. */}
                                <h3 class="text-xl font-semibold tracking-[-0.02em] lg:sr-only">
                                    {content.glossary.title}
                                </h3>
                                <dl class="mt-3 border-t border-[#e6e3db] lg:mt-0">
                                    {content.glossary.items.map((entry) => (
                                        <div key={entry.term} class="border-b border-[#e6e3db] py-3">
                                            <dt class="text-sm font-bold">{entry.term}</dt>
                                            <dd class="mt-1 text-[13px] leading-[1.55] text-[#66696b] lg:text-[13.5px]">
                                                {entry.definition}
                                            </dd>
                                        </div>
                                    ))}
                                </dl>
                            </>
                        }
                    />

                    {/* FINAL CTA */}
                    <section class={`${PAGE_CONTAINER_CLASS} ${SECTION_SPACING_CLASS}`}>
                        <div
                            data-reveal
                            class="ud-dark-block rounded-[30px] px-[22px] py-8 shadow-[0_30px_70px_rgba(27,29,30,0.18)] lg:grid lg:grid-cols-[minmax(0,1.2fr)_auto] lg:items-center lg:gap-12 lg:rounded-[36px] lg:p-16"
                        >
                            <div>
                                <h2 class="text-[30px] font-medium leading-[1.1] tracking-[-0.03em] text-white lg:text-[44px] lg:leading-[1.08] lg:tracking-[-0.035em]">
                                    {content.finalCta.title}
                                </h2>
                                <p class="mt-3.5 text-[15px] leading-[1.6] text-white/70 lg:mt-4 lg:text-[17px]">
                                    {content.finalCta.body}
                                </p>
                            </div>
                            <CtaPill
                                href={links.productDomains}
                                variant="accent-inverted"
                                size="lg"
                                width="full"
                                class="mt-[22px] lg:mt-0 lg:w-fit"
                                onClick={() =>
                                    trackCta({
                                        section: 'final_cta',
                                        label: content.finalCta.ctaLabel,
                                        kind: 'product',
                                        href: links.productDomains,
                                        triggerId: 'website_product_appraise_final_get_started',
                                    })
                                }
                            >
                                {content.finalCta.ctaLabel}
                            </CtaPill>
                        </div>
                    </section>

                    {/* Required by the internal-linking contract, appended after
                        the mockup's last section rather than interleaved. */}
                    <RelatedLinks links={relatedLinks} title="Continue the workflow" />

                    <HomeAskAiSection context={askAiContext} />

                    <PageGeoBlock className="pb-4 pt-[100px]" geo={geoContext} surface="product_appraise" />
                </main>

                <Footer links={links} sectionHrefPrefix="/" trackingPage={TRACKING_PAGE} />
            </div>
        </div>
    );
};

export default ProductAppraisePage;
